import { useCallback, useEffect, useState } from 'react';
import { del, get, post } from '../lib/api';
import { Bar, ErrorNote, Empty, Ring, Spinner } from '../components/ui';
import ChoreList from '../components/ChoreList';
import { money, percent } from '../lib/format';
import type { Board, Occurrence, TickResult } from '../lib/types';

export function tone(summary: { rate: number; threshold: number }): 'good' | 'warn' | 'bad' {
  if (summary.rate + 1e-9 >= summary.threshold) return 'good';
  if (summary.rate >= summary.threshold * 0.6) return 'warn';
  return 'bad';
}

export function useBoard(childId?: number): {
  board: Board | null;
  error: string | null;
  reload: () => Promise<void>;
  toggle: (occurrence: Occurrence, done: boolean) => Promise<TickResult | null>;
  snooze: (on: boolean) => Promise<void>;
} {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const query = childId ? `?childId=${childId}` : '';
      const res = await get<{ board: Board }>(`/api/board${query}`);
      setBoard(res.board);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [childId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggle = useCallback(
    async (occurrence: Occurrence, done: boolean): Promise<TickResult | null> => {
      // Optimistic: the tick should feel instant on a phone.
      setBoard((current) => (current ? applyToggle(current, occurrence, done) : current));
      let result: TickResult | null = null;
      try {
        result = await post<TickResult>('/api/chores/completions', {
          choreId: occurrence.choreId,
          periodKey: occurrence.periodKey,
          done,
        });
      } catch (err) {
        setError((err as Error).message);
      }
      await reload();
      return result;
    },
    [reload],
  );

  // Pause today's reminders (a kid can do this one day at a time).
  const snooze = useCallback(
    async (on: boolean) => {
      try {
        if (on) await post('/api/reminders/snooze', childId ? { childId } : {});
        else await del(`/api/reminders/snooze${childId ? `?childId=${childId}` : ''}`);
      } catch (err) {
        setError((err as Error).message);
      }
      await reload();
    },
    [childId, reload],
  );

  return { board, error, reload, toggle, snooze };
}

function applyToggle(board: Board, occurrence: Occurrence, done: boolean): Board {
  const key = `${occurrence.choreId}:${occurrence.periodKey}`;
  const without = (list: Occurrence[]) => list.filter((o) => `${o.choreId}:${o.periodKey}` !== key);
  if (done) {
    return {
      ...board,
      overdue: without(board.overdue),
      dueToday: without(board.dueToday),
      soon: without(board.soon),
      restOfPeriod: without(board.restOfPeriod),
      later: without(board.later),
      doneToday: [...board.doneToday, { ...occurrence, state: 'done' }],
    };
  }
  const back: Occurrence = {
    ...occurrence,
    state: occurrence.dueDate < board.date ? 'overdue' : occurrence.dueDate === board.date ? 'due_today' : 'upcoming',
  };
  return {
    ...board,
    doneToday: without(board.doneToday),
    overdue: back.state === 'overdue' ? [...board.overdue, back] : board.overdue,
    dueToday: back.state === 'due_today' ? [...board.dueToday, back] : board.dueToday,
    soon: back.state === 'upcoming' ? [...board.soon, back] : board.soon,
  };
}

export default function KidHome(): JSX.Element {
  const { board, error, toggle, snooze } = useBoard();
  if (error && !board) return <div className="app"><ErrorNote error={error} /></div>;
  if (!board) return <Spinner />;
  return <KidBoardView board={board} onToggle={toggle} onSnooze={snooze} error={error} />;
}

export function KidBoardView({
  board,
  onToggle,
  onSnooze,
  error,
  readOnly,
}: {
  board: Board;
  onToggle: (occurrence: Occurrence, done: boolean) => Promise<unknown>;
  onSnooze?: (on: boolean) => Promise<void>;
  error?: string | null;
  readOnly?: boolean;
}): JSX.Element {
  const { week, month } = board;
  // Everything still to come inside this pay period, so the list adds up to
  // what the money bar is counting.
  const ahead = [...board.soon, ...board.restOfPeriod];
  const nothingLeft = board.overdue.length + board.dueToday.length + ahead.length === 0;
  const useMonthly = month.allowance > 0 && week.allowance === 0;
  const payPeriod = useMonthly ? month : week;

  return (
    <>
      <ErrorNote error={error ?? null} />

      <div className="card">
        <div className="row between">
          <div className="grow">
            <h3 style={{ marginBottom: 2 }}>{useMonthly ? 'This month' : 'This week'}</h3>
            <div className="small muted">
              {payPeriod.doneCount} of {payPeriod.totalCount} chores done
              {payPeriod.missedCount > 0 && ` · ${payPeriod.missedCount} missed`}
            </div>
          </div>
          <Ring value={payPeriod.rate} tone={tone(payPeriod)} />
        </div>
        <div style={{ marginTop: 12 }}>
          <Bar value={payPeriod.rate} tone={tone(payPeriod)} />
        </div>
        {payPeriod.allowance > 0 && (
          <div className="row between wrap" style={{ marginTop: 12 }}>
            <span className="small muted">
              Pocket money needs {percent(payPeriod.threshold)}
            </span>
            <span className={`badge ${payPeriod.qualifies ? 'good' : 'bad'}`}>
              {payPeriod.qualifies ? '✅ On track' : '❌ Not yet'} · {money(payPeriod.amount, payPeriod.currency)}
            </span>
          </div>
        )}
      </div>

      {board.quietToday && (
        <div className="notice" style={{ marginTop: 10 }}>
          🏅 {board.quietToday} today — no reminders will be sent.
        </div>
      )}

      {board.snoozedUntil ? (
        <div className="notice row between wrap" style={{ marginTop: 10 }}>
          <span>🔕 Reminders are off until tomorrow. Chores still count.</span>
          {onSnooze && (
            <button className="btn ghost small" type="button" onClick={() => void onSnooze(false)}>
              Turn back on
            </button>
          )}
        </div>
      ) : (
        !board.quietToday &&
        onSnooze &&
        nothingLeft === false && (
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
            <button className="btn ghost small" type="button" onClick={() => void onSnooze(true)}>
              🔕 Snooze reminders until tomorrow
            </button>
          </div>
        )
      )}

      {board.overdue.length > 0 && (
        <>
          <h2>Late</h2>
          <ChoreList items={board.overdue} today={board.date} onToggle={onToggle} readOnly={readOnly} />
        </>
      )}

      <h2>Today</h2>
      {board.dueToday.length > 0 ? (
        <ChoreList items={board.dueToday} today={board.date} onToggle={onToggle} readOnly={readOnly} />
      ) : (
        <Empty icon={nothingLeft ? '🎉' : '👍'}>
          {nothingLeft ? 'Everything done. Nice one!' : 'Nothing due today.'}
        </Empty>
      )}

      {ahead.length > 0 && (
        <>
          <h2>Coming up</h2>
          <ChoreList items={ahead} today={board.date} onToggle={onToggle} readOnly={readOnly} />
        </>
      )}

      {board.doneToday.length > 0 && (
        <>
          <h2>Done today</h2>
          <ChoreList items={board.doneToday} today={board.date} onToggle={onToggle} readOnly={readOnly} />
        </>
      )}

      {board.later.length > 0 && (
        <>
          <h2>Later this month</h2>
          <ChoreList items={board.later.slice(0, 8)} today={board.date} onToggle={onToggle} readOnly={readOnly} />
        </>
      )}
    </>
  );
}
